/** tm/components/TagEditor.tsx — 태그 입력/편집 (필수/권장 2존 + 프리셋) + FP 자동완성 */
import { useState, useRef, useEffect } from 'react';
import type { FP } from '../types';
import { C, T, R, Badge } from './ui';
import * as api from '../api';

// ═══ TagEditor ═══
interface TagsStructured {
  required: string[];
  recommended: string[];
  interests: string[];
}

interface TagEditorProps {
  tags: string[] | TagsStructured;
  onChange: (tags: TagsStructured) => void;
  flat?: boolean;
  suggestions?: string[];
  placeholder?: string;
  onCustomTag?: (tag: string) => void;
}

export const COMPANY_SUGGEST_TAGS: Record<string, string[]> = {
  '업종': ['제조업', 'IT/SW', '서비스업', '소상공인', '농식품', '바이오', '건설업', '수산업', '관광', '문화'],
  '특성': ['수출기업', 'R&D', '벤처인증', '이노비즈', '메인비즈', '스타트업', '사회적기업', '여성기업'],
  '규모': ['중소기업', '소기업', '중기업', '1인기업', '프랜차이즈'],
  '니즈': ['인건비', '융자', '시설장비', '특허', '마케팅', '디지털전환', 'ESG', '컨설팅', '인증'],
  '지역': ['서울', '경기', '인천', '부산', '대구', '대전', '광주', '울산', '세종', '강원', '충북', '충남', '전북', '전남', '경북', '경남', '제주'],
};

function normalizeTagsInput(tags: string[] | TagsStructured): TagsStructured {
  if (typeof tags === 'string') { try { tags = JSON.parse(tags); } catch { return { required: [], recommended: [], interests: [] }; } }
  if (Array.isArray(tags)) return { required: tags, recommended: [], interests: [] };
  if (tags && typeof tags === 'object' && 'required' in tags) return { required: tags.required || [], recommended: tags.recommended || [], interests: (tags as any).interests || [] };
  return { required: [], recommended: [], interests: [] };
}

export function TagEditor({ tags: rawTags, onChange, suggestions, placeholder = '태그 입력', onCustomTag, flat }: TagEditorProps) {
  const structured = normalizeTagsInput(rawTags);
  const [activeZone, setActiveZone] = useState<'required' | 'recommended' | 'interests'>('required');
  const [input, setInput] = useState('');
  const [showSuggest, setShowSuggest] = useState(false);
  const suggestRef = useRef<HTMLDivElement>(null);
  const [presets, setPresets] = useState<api.TagPreset[]>([]);

  // Close suggest on outside click
  useEffect(() => {
    function handleOutsideClick(e: MouseEvent) {
      if (suggestRef.current && !suggestRef.current.contains(e.target as Node)) {
        setShowSuggest(false);
      }
    }
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  // Fetch tag presets on mount
  useEffect(() => {
    api.getTagPresets().then(res => {
      if (res.data) setPresets(res.data);
    }).catch(() => {});
  }, []);

  // Group presets by category
  const presetsByCategory: Record<string, api.TagPreset[]> = {};
  presets.forEach(p => {
    const cat = p.category || '기타';
    if (!presetsByCategory[cat]) presetsByCategory[cat] = [];
    presetsByCategory[cat].push(p);
  });

  const activeTags = flat ? [...structured.required, ...structured.recommended, ...structured.interests] : structured[activeZone];
  const allTags = [...structured.required, ...structured.recommended, ...structured.interests];

  // Merge preset labels + suggestions for filtering
  const presetLabels = presets.map(p => p.label);
  const allSuggestions = Array.from(new Set([...(suggestions || []), ...presetLabels]));
  const filtered = allSuggestions.filter(s => !allTags.includes(s) && (!input || s.includes(input)));

  const zoneColor = flat ? C.accent : activeZone === 'required' ? C.accent : activeZone === 'recommended' ? C.green : '#D97706';
  const zoneBg = flat ? C.accentLt : activeZone === 'required' ? C.accentLt : activeZone === 'recommended' ? C.greenLt : '#FFFBEB';

  function addTag(tag: string) {
    const t = tag.trim();
    if (!t || activeTags.includes(t)) return;
    // Remove from other zone if present
    const updated = { ...structured };
    const zones: ('required' | 'recommended' | 'interests')[] = ['required', 'recommended', 'interests'];
    zones.forEach(z => { if (z !== activeZone) updated[z] = updated[z].filter(x => x !== t); });
    updated[activeZone] = [...updated[activeZone], t];
    onChange(updated);
    setInput('');
  }

  function removeTag(tag: string, zone: 'required' | 'recommended' | 'interests') {
    const updated = { ...structured, [zone]: structured[zone].filter(t => t !== tag) };
    onChange(updated);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && input.trim()) {
      e.preventDefault();
      const tag = input.trim();
      addTag(tag);
      // Save as preset if custom tag
      if (!allSuggestions.includes(tag)) {
        api.createTagPreset({ label: tag, category: 'custom' }).then(res => {
          if (res.data) setPresets(p => [...p, res.data]);
        }).catch(() => {});
        if (onCustomTag) onCustomTag(tag);
      }
    } else if (e.key === 'Backspace' && !input && activeTags.length > 0) {
      removeTag(activeTags[activeTags.length - 1], activeZone);
    }
  }

  return (
    <div>
      {/* Zone toggle tabs */}
      {!flat && <div style={{ display: 'flex', borderBottom: '1px solid #E5E7EB', marginBottom: 8 }}>
        {([['required', '필수조건', C.accent], ['recommended', '권장조건', C.green], ['interests', '관심사', '#D97706']] as [typeof activeZone, string, string][]).map(([zone, label, color]) => (
          <button key={zone} onClick={() => setActiveZone(zone)}
            style={{ padding: '6px 12px', fontSize: T.xs, fontWeight: 600, border: 'none', background: 'transparent', cursor: 'pointer', color: activeZone === zone ? color : '#9CA3AF', borderBottom: activeZone === zone ? `2px solid ${color}` : '2px solid transparent' }}>
            {label} ({structured[zone].length})
          </button>
        ))}
      </div>}

      {/* Active zone tag chips + input */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 4, padding: '6px 10px',
        borderRadius: R.md, border: `1px solid ${C.border}`, background: C.white,
        minHeight: 36, alignItems: 'center', cursor: 'text',
      }} onClick={() => setShowSuggest(true)}>
        {activeTags.map(tag => (
          <span key={tag} style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '2px 8px', borderRadius: 10, background: '#F3F4F6',
            color: '#374151', fontSize: T.xs, border: '1px solid #E5E7EB',
          }}>
            {tag}
            <button onClick={(e) => { e.stopPropagation(); removeTag(tag, activeZone); }}
              style={{ background: 'none', border: 'none', color: zoneColor, cursor: 'pointer', fontSize: T.xs, padding: 0 }}>✕</button>
          </span>
        ))}
        <input value={input} onChange={e => { setInput(e.target.value); setShowSuggest(true); }}
          onKeyDown={handleKeyDown} onFocus={() => setShowSuggest(true)}
          placeholder={activeTags.length === 0 ? placeholder : ''}
          style={{ border: 'none', outline: 'none', fontSize: T.xs, flex: 1, minWidth: 60, padding: '2px 0' }} />
      </div>

      {/* Other zones summary */}
      {!flat && (['required', 'recommended', 'interests'] as const).filter(z => z !== activeZone && structured[z].length > 0).map(z => {
        const zLabel = z === 'required' ? '필수' : z === 'recommended' ? '권장' : '관심';
        const zColor = z === 'required' ? C.accent : z === 'recommended' ? C.green : '#D97706';
        const zBg = z === 'required' ? C.accentLt : z === 'recommended' ? C.greenLt : '#FFFBEB';
        return (
          <div key={z} style={{ marginTop: 6, fontSize: T.xs, color: C.muted }}>
            {zLabel}: {structured[z].map(t => (
              <span key={t} style={{ display: 'inline-block', padding: '1px 6px', borderRadius: 4, background: zBg, color: zColor, fontSize: T.xs, marginRight: 2, fontWeight: 600 }}>{t}</span>
            ))}
          </div>
        );
      })}

      {/* Presets + suggestions dropdown */}
      {showSuggest && (
        <div ref={suggestRef} style={{
          marginTop: 4, padding: 8, borderRadius: R.sm, border: `1px solid ${C.border}`,
          background: C.bgAlt, maxHeight: 220, overflowY: 'auto',
        }}>
          {input.trim() && filtered.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginBottom: 6 }}>
              {filtered.slice(0, 8).map(s => (
                <button key={s} onClick={() => addTag(s)} style={{ padding: '2px 8px', borderRadius: 10, border: `1px solid ${zoneColor}44`, background: zoneBg, fontSize: T.xs, cursor: 'pointer', color: zoneColor }}>+ {s}</button>
              ))}
            </div>
          )}
          {/* Preset tags by category */}
          {!input.trim() && Object.keys(presetsByCategory).length > 0 && (
            <>
              {Object.entries(presetsByCategory).map(([cat, items]) => {
                return (
                  <div key={cat} style={{ marginBottom: 5 }}>
                    <div style={{ fontSize: T.xs, fontWeight: 700, color: C.muted, marginBottom: 2, textTransform: 'uppercase' }}>{cat}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                      {items.map(p => {
                        const selected = allTags.includes(p.label);
                        return (
                          <button key={p.id} onClick={() => selected ? removeTag(p.label, structured.required.includes(p.label) ? 'required' : 'recommended') : addTag(p.label)} style={{ padding: '3px 8px', borderRadius: 10, border: selected ? '1px solid #BFDBFE' : '1px solid #E5E7EB', background: selected ? '#EFF6FF' : 'white', fontSize: T.xs, cursor: 'pointer', color: selected ? C.accent : '#6B7280', fontWeight: selected ? 700 : 400 }}>{selected ? '✓ ' : '+ '}{p.label}</button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </>
          )}
          {/* Fallback to hardcoded COMPANY_SUGGEST_TAGS */}
          {!input.trim() && Object.entries(COMPANY_SUGGEST_TAGS).map(([cat, items]) => {
            return (
              <div key={cat} style={{ marginBottom: 5 }}>
                <div style={{ fontSize: T.xs, fontWeight: 700, color: C.muted, marginBottom: 2, textTransform: 'uppercase' }}>{cat}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                  {items.map(t => {
                    const selected = allTags.includes(t);
                    return (
                      <button key={t} onClick={() => selected ? removeTag(t, structured.required.includes(t) ? 'required' : 'recommended') : addTag(t)} style={{ padding: '3px 8px', borderRadius: 10, border: selected ? '1px solid #BFDBFE' : '1px solid #E5E7EB', background: selected ? '#EFF6FF' : 'white', fontSize: T.xs, cursor: 'pointer', color: selected ? C.accent : '#6B7280', fontWeight: selected ? 700 : 400 }}>{selected ? '✓ ' : '+ '}{t}</button>
                    );
                  })}
                </div>
              </div>
            );
          })}
          <button onClick={() => setShowSuggest(false)} style={{ marginTop: 4, fontSize: T.xs, color: C.muted, background: 'none', border: 'none', cursor: 'pointer' }}>접기</button>
        </div>
      )}
    </div>
  );
}

// ═══ FpAutocomplete ═══
interface FpAutoProps {
  fps: FP[];
  value: string;
  onChange: (fpId: string) => void;
  placeholder?: string;
}

export function FpAutocomplete({ fps, value, onChange, placeholder = 'FP 검색' }: FpAutoProps) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const selected = fps.find(f => f.id === value);
  const filtered = fps.filter(f =>
    !search || f.name.includes(search) || (f.company || '').includes(search) || (f.phone || '').includes(search)
  );

  useEffect(() => {
    function close(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div onClick={() => setOpen(!open)} style={{
        padding: '8px 12px', borderRadius: R.md, border: `1px solid ${C.border}`,
        background: C.white, cursor: 'pointer', fontSize: T.sm,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <span style={{ color: selected ? C.text : C.muted }}>
          {selected ? `${selected.name} (${selected.company || '-'})` : placeholder}
        </span>
        <span style={{ fontSize: T.xs, color: C.muted }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
          borderRadius: R.sm, border: `1px solid ${C.border}`, background: C.white,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: 200, overflow: 'hidden',
        }}>
          <input value={search} onChange={e => setSearch(e.target.value)} autoFocus
            placeholder="이름/소속/연락처 검색"
            style={{ width: '100%', padding: '8px 12px', border: 'none', borderBottom: `1px solid ${C.border}`, fontSize: T.xs, outline: 'none', boxSizing: 'border-box' }} />
          <div style={{ maxHeight: 150, overflowY: 'auto' }}>
            <div onClick={() => { onChange(''); setOpen(false); setSearch(''); }}
              style={{ padding: '6px 12px', cursor: 'pointer', fontSize: T.xs, color: C.muted, borderBottom: `1px solid ${C.bg}` }}>선택 안 함</div>
            {filtered.map(f => (
              <div key={f.id} onClick={() => { onChange(f.id); setOpen(false); setSearch(''); }}
                style={{
                  padding: '6px 12px', cursor: 'pointer', fontSize: T.xs,
                  borderBottom: `1px solid ${C.bg}`,
                  background: f.id === value ? C.accentLt : 'transparent',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = C.bg)}
                onMouseLeave={e => (e.currentTarget.style.background = f.id === value ? C.accentLt : '')}>
                <div style={{ fontWeight: 600 }}>{f.name}</div>
                <div style={{ fontSize: T.xs, color: C.muted }}>{f.company || '-'} · {f.phone || '-'}</div>
              </div>
            ))}
            {filtered.length === 0 && <div style={{ padding: 12, textAlign: 'center', color: C.muted, fontSize: T.xs }}>결과 없음</div>}
          </div>
        </div>
      )}
    </div>
  );
}
